import { hubBrandUuidForRequest } from '@/store';
import logger from '@/utils/logger';
import { getRegisteredFetchToken } from './fetchTokenRegistry';
import { removeAccessToken } from './tokenStorage';

// gloss
const SKIP_RETRY_PATTERNS = ['/auth/', '/oauth/'];

const shouldSkipRetry = (url: string): boolean => SKIP_RETRY_PATTERNS.some((pattern) => url.includes(pattern));

let refreshPromise: Promise<string | null> | null = null;

const getOrStartRefresh = (): Promise<string | null> => {
  const fetchToken = getRegisteredFetchToken();
  if (!fetchToken) return Promise.resolve(null);

  if (refreshPromise) return refreshPromise;

  refreshPromise = fetchToken().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

// gloss
const withHubBrandHeader = (input: RequestInfo | URL, init?: RequestInit): RequestInit | undefined => {
  const hubBrandUuid = hubBrandUuidForRequest();
  if (!hubBrandUuid) return init;

  const headers = new Headers(input instanceof Request ? input.headers : undefined);
  new Headers(init?.headers).forEach((value, key) => headers.set(key, value));
  headers.set('x-brand-uuid', hubBrandUuid);

  return { ...init, headers };
};

// gloss
export const authFetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  const clonedInput = input instanceof Request ? input.clone() : input;
  const initWithBrand = withHubBrandHeader(input, init);
  const response = await fetch(input, initWithBrand);

  if (response.status !== 401) return response;

  const url =
    typeof clonedInput === 'string'
      ? clonedInput
      : clonedInput instanceof URL
        ? clonedInput.toString()
        : clonedInput.url;
  if (shouldSkipRetry(url)) return response;

  try {
    removeAccessToken();
    const freshToken = await getOrStartRefresh();
    if (!freshToken) return response;

    const baseHeaders = new Headers(clonedInput instanceof Request ? clonedInput.headers : undefined);
    new Headers(initWithBrand?.headers).forEach((value, key) => baseHeaders.set(key, value));
    baseHeaders.set('Authorization', `Bearer ${freshToken}`);

    const retryInit: RequestInit = {
      ...initWithBrand,
      headers: baseHeaders,
    };

    return await fetch(clonedInput, retryInit);
  } catch (error) {
    logger.error(error, { context: 'authFetch', url });
    return response;
  }
};
